/**
 * ProtectedRoute - Route guard component for authentication
 * Extracted from App.js for better maintainability
 */
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const ProtectedRoute = ({ children, requirePlatformAdmin = false, requireAdmin = false, requireAdminOrSupervisor = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Check if platform admin access is required
  if (requirePlatformAdmin) {
    // Only super_admin or platform_admin roles allowed
    const isPlatformAdmin = user.role === 'super_admin' || user.role === 'platform_admin';
    
    // Additional security: Organization admins should NOT access platform routes
    // Even if role looks correct, check if user has organization_id (org-scoped user)
    const isOrgScoped = user.organization_id && user.organization_id !== '';
    
    if (!isPlatformAdmin || isOrgScoped) {
      console.warn('🔐 SECURITY: Platform admin access denied. Redirecting to dashboard.', {
        role: user.role,
        hasOrgId: !!user.organization_id
      });
      return <Navigate to="/dashboard" />;
    }
  }

  // Check if admin access is required (includes super_admin)
  if (requireAdmin && user.role !== 'admin' && user.role !== 'super_admin') {
    return <Navigate to="/dashboard" />;
  }

  // Phase 2: admin OR supervisor. Used by the assignment board & my-team.
  if (requireAdminOrSupervisor
      && !['admin', 'super_admin', 'supervisor'].includes(user.role)
      && !user.is_supervisor) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

export default ProtectedRoute;
